import { APIResponse } from "@/types/drivers/api"

export const mostCommonCategory = (arr: APIResponse["data"]["recentTrips"]) => {
  if (!arr.length) return undefined

  const categoryCounts = arr.reduce<Record<string, number>>(
    (totals, { category }) => {
      totals[category] = (totals[category] ?? 0) + 1
      return totals
    },
    {},
  )

  const maxCount = Math.max(...Object.values(categoryCounts))
  const topCategories = Object.entries(categoryCounts)
    .filter(([, count]) => count === maxCount)
    .map(([category]) => category)

  if (topCategories.length === 1) return topCategories[0]

  const priorityOrder = [
    { check: (cat: string) => cat.toLowerCase().includes("pet"), label: "pet" },
    {
      check: (cat: string) => cat.toLowerCase().includes("boat"),
      label: "boat",
    },
    {
      check: (cat: string) => cat.toLowerCase() === "vehicles",
      label: "Vehicles",
    },
    {
      check: (cat: string) => cat.toLowerCase().includes("motor"),
      label: "motor",
    },
    {
      check: (cat: string) => cat.toLowerCase().includes("household"),
      label: "household",
    },
    {
      check: (cat: string) => cat.toLowerCase().includes("apartment"),
      label: "apartment",
    },
    {
      check: (cat: string) => cat.toLowerCase().includes("freight"),
      label: "freight",
    },
    {
      check: (cat: string) => cat.toLowerCase().includes("heavy"),
      label: "heavy",
    },
    {
      check: (cat: string) => cat.toLowerCase().includes("all other"),
      label: "all other",
    },
  ]

  for (const { check } of priorityOrder) {
    const matchingCategory = topCategories.find((cat) => check(cat))
    if (matchingCategory) return matchingCategory
  }

  return topCategories[0]
}

export const driverDescription = (data: APIResponse["data"]): string => {
  let description = []

  if (data.rating && data.ratingsCount) {
    let status = data.rating >= 4.5 ? "top performer" : "professional"
    description.push(
      `Highly rated ${status} with a ${data.rating.toFixed(1)}/5 rating from ${
        data.ratingsCount
      } customer reviews`,
    )
  }

  if (data.wonShipments) {
    description.push(`Successfully completed ${data.wonShipments} shipments`)
  }

  // Car info
  if (data.cars?.[0]?.make_name && data.cars[0].model_name) {
    let car = data.cars[0]
    let year = car.variant_year ? `${car.variant_year} ` : ""
    description.push(`Operates a ${year}${car.make_name} ${car.model_name}`)
  }

  // Services info
  if (data.offerings?.[0]?.services?.length) {
    let services = data.offerings[0].services
      .map((s) => s.name)
      .filter((name) => name)
      .slice(0, 2)
    if (services.length) {
      description.push(
        `Specializes in ${services.join(" and ")} transportation`,
      )
    }
  }

  if (data.recentTrips) {
    const mostListed = mostCommonCategory(data.recentTrips)
    description.push(`Mostly delivers ${mostListed}`)
  }

  // Combine or default
  return description.length
    ? description.join(". ") + "."
    : "Professional transport driver providing reliable shipping services."
}

export const formatReviewDate = (createdAt: string) => {
  const reviewDate = new Date(createdAt) as any
  const diffMs = (new Date() as any) - reviewDate
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)
  const diffYears = Math.floor(diffDays / 365)

  if (diffSeconds < 60)
    return `${diffSeconds} second${diffSeconds === 1 ? "" : "s"} ago`
  if (diffMinutes < 60)
    return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`
  if (diffHours < 24)
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`
  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks === 1 ? "" : "s"} ago`
  if (diffMonths < 12)
    return `${diffMonths} month${diffMonths === 1 ? "" : "s"} ago`
  return `${diffYears} year${diffYears === 1 ? "" : "s"} ago`
}

export default formatReviewDate
